// Pure formatting of what the shell's Rust side answers (decision 064); unit-tested.

import { maintenanceLabel, crewRoleLabel } from "@/lib/format";

import type { CheckoutOutcome, InstallOutcome, LiveryOutcome, SessionEvent, StatusRow, TransferMessage, WatchEvent } from "./types";

/** The host of a platform address, for the header and the status bar. */
export function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/** A page of the web app, from the platform address and an absolute path. */
export function webUrl(apiUrl: string, path: string): string {
  return `${apiUrl.replace(/\/+$/, "")}${path.startsWith("/") ? path : `/${path}`}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function classificationLabel(row: Pick<StatusRow, "classification">): string {
  switch (row.classification) {
    case "personal":
      return "personal";
    case "shared_by_me":
      return "shared by me";
    case "shared_with_me":
      return "shared with me";
    default:
      return "unknown";
  }
}

/** "23:48" (or "tomorrow 09:15"): a time of this PC's clock, in the pilot's zone. */
export function localClock(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay ? time : `${d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" })} ${time}`;
}

/** The booking line of a row: yours and until when, or nothing. */
export function leaseLine(row: StatusRow, now: Date = new Date()): string | null {
  const mine = row.local?.lease;
  if (mine) return `yours until ${localClock(mine.expires_at, now)}`;
  return null;
}

export function installLine(install: InstallOutcome): string {
  switch (install.action) {
    case "lease_only":
      return "the aircraft is yours, the files untouched (read-only mode)";
    case "metadata_only":
      return `entry ${install.version.seq} carries numbers only, no files; the local files stay${install.last_used ? ", selected first" : ""}`;
    case "already_installed":
      return `entry ${install.version.seq} already installed${install.last_used ? ", selected first" : ""}`;
    case "installed":
      return `entry ${install.version.seq} installed (${install.files} files${install.version.bytes ? `, ${formatBytes(install.version.bytes)}` : ""}); the files that were here are kept as a backup`;
  }
}

export function checkoutLine(outcome: CheckoutOutcome, now: Date = new Date()): string {
  return `${bookedLine(outcome, now)}. Start X-Plane and fly; ShariFly will upload the aircraft when you close X-Plane parked.`;
}

/** The booking alone, for a booking whose watch did not start. */
export function bookedLine(outcome: CheckoutOutcome, now: Date = new Date()): string {
  return `Yours until ${localClock(outcome.lease.expires_at, now)}: ${installLine(outcome.install)}${liveryLine(outcome.livery)}`;
}

/** What the check-out did about the livery (F407), as a clause, or nothing. */
export function liveryLine(outcome: LiveryOutcome | undefined): string {
  if (!outcome || outcome.action === "none") return "";
  if (outcome.action === "present") return `; livery "${outcome.name}" already here`;
  if (outcome.action === "left_out") return `; livery "${outcome.name}" left out (your setting: Install the liveries owners offer)`;
  if (outcome.action === "blocked") return `; livery "${outcome.name}" waits for X-Plane to be closed`;
  if (outcome.action === "failed") return `; livery${outcome.name ? ` "${outcome.name}"` : ""} not installed: ${outcome.error}`;
  return `; livery "${outcome.name}" installed (${outcome.files} files, ${formatBytes(outcome.bytes)})`;
}

/** "Downloading the package, 12.3 of 48.0 MB" (F407). */
export function transferLine(t: TransferMessage): string {
  const verb = t.direction === "download" ? "Downloading" : "Uploading";
  return `${verb} the ${t.what}, ${formatBytes(t.done)} of ${formatBytes(t.total)}`;
}

/** "Cabin cleaning under way, done in 12 s" (F406). */
export function inProgressLine(job: { action: string; completes_at: string }, now: Date = new Date()): string {
  const label = maintenanceLabel(job.action);
  const left = Math.ceil((new Date(job.completes_at).getTime() - now.getTime()) / 1000);
  const when = left <= 0 ? "done any moment now" : `done in ${left} s`;
  return `${label[0].toUpperCase()}${label.slice(1)} under way, ${when}`;
}

/** One readable line per watch event (the Rust `WatchEvent`, F405). */
export function watchLine(event: WatchEvent): string {
  const d = (event.data ?? {}) as Record<string, unknown>;
  switch (event.type) {
    case "holder":
      return `watching ${d.name || d.handle}'s flight (the aircraft is theirs until ${d.expires_at})`;
    case "install":
      return `their state here: ${installLine(d as unknown as InstallOutcome)}`;
    case "livery":
      // The pilot's livery (F407), left out by the setting or not (F487), by name.
      return `their livery${liveryLine(d as unknown as LiveryOutcome).replace(/^; livery/, "") || ": nothing to install"}`;
    case "live":
      return `live: windshield ${Math.round(Number(d.windshield_bugs) * 100)}%, cabin ${Math.round(Number(d.cabin_dirt) * 100)}% (update ${d.seq})`;
    case "waiting":
      return "no live value from the pilot yet";
    case "plugin":
      return d.linked ? "the live dirt is shown in the cockpit" : "the plugin did not take the live dirt";
    case "plugin_behind":
      return pluginBehindLine(d);
    case "crew":
      return crewLine(d);
    default:
      return event.type;
  }
}

/** This PC's seat in the shared cockpit (F470), as the aircraft said it and the platform was told. */
function crewLine(d: Record<string, unknown>): string {
  return `in the shared cockpit as ${crewRoleLabel(Number(d.role), Number(d.pilot_seat))}${d.guest ? " (a guest: the host's app records the flight)" : " (the host)"}`;
}

/** The plugin in the cockpit is in step but behind the newest release (F412): said, not refused. */
function pluginBehindLine(d: Record<string, unknown>): string {
  return `the plugin in X-Plane is ${d.found}; ${d.latest} is out: install the newest plugin zip with X-Plane closed`;
}

/** One readable line per session event (the Rust `SessionEvent`). */
export function sessionLine(event: SessionEvent): string {
  const d = (event.data ?? {}) as Record<string, unknown>;
  switch (event.type) {
    case "lease_acquired":
      return `the aircraft is yours until ${d.expires_at}`;
    case "lease_reused":
      return "the aircraft was already yours";
    case "heartbeat":
      return `heartbeat: yours until ${d.expires_at}`;
    case "offline":
      return `offline (${d.error}); the aircraft stays yours until ${d.expires_at}, trying again`;
    case "waiting_for_sim":
      return "waiting for X-Plane to start";
    case "sim_running":
      return "X-Plane is running";
    case "sim_closed":
      return "X-Plane is closed; waiting for the files to settle";
    case "sim_lost_in_flight":
      return `X-Plane went away with the aircraft in the air: the booking stands for ${d.minutes_left} more minutes; start X-Plane again with the aircraft to go on, or nothing of this flight is written`;
    case "sim_back":
      return "X-Plane is back: the flight goes on";
    case "waiting_quiet":
      return `${d.seconds_left} s of quiet to go`;
    case "track_attached":
      return `${d.samples} recorder samples go with the return`;
    case "spool_attached":
      return `${d.events} plugin events go with the return`;
    case "checking_in":
      return "returning the aircraft";
    case "plugin":
      return d.linked
        ? "the cockpit shows the aircraft as yours (SharedAirframe plugin)"
        : "the SharedAirframe plugin is not reachable; the cockpit does not show the aircraft as yours";
    case "plugin_behind":
      return pluginBehindLine(d);
    case "checkin": {
      const inner = d as SessionEvent;
      return checkinLine(inner);
    }
    case "maintenance_started":
      return `${maintenanceLabel(String(d.action))} started, done at ${d.completes_at}; the aircraft is not to be flown meanwhile`;
    case "maintenance_done":
      return `${maintenanceLabel(String(d.action))} done: the cockpit shows it`;
    case "maintenance_refused":
      return `${maintenanceLabel(String(d.action))} refused: ${d.error}`;
    case "cockpit_shared":
      return `the shared cockpit's key shared with the members${d.name ? ` (host ${d.name})` : ""}: they see it on the airframe's page`;
    case "crew":
      return crewLine(d);
    default:
      return event.type;
  }
}

/** One readable line per check-in event (the Rust `CheckinEvent`). */
export function checkinLine(event: SessionEvent): string {
  const d = (event.data ?? {}) as Record<string, unknown>;
  switch (event.type) {
    case "resuming":
      return `resuming the return waiting in ${d.path}${d.uploaded ? " (already uploaded)" : ""}`;
    case "stale_entry_kept":
      return `a return from an earlier booking was held on this PC in ${d.path}`;
    case "entry_replaced":
      return `the files changed since the last attempt; kept aside in ${d.path}, packing again`;
    case "warning":
      return `warning: ${String(event.data)}`;
    case "packed":
      return `packed ${d.files} files (${formatBytes(Number(d.bytes))})`;
    case "uploading":
      return `uploading ${formatBytes(Number(d.bytes))} (attempt ${d.attempt})`;
    case "uploaded":
      return "uploaded";
    case "retrying": {
      const wait = d.wait as { secs?: number } | undefined;
      return `no answer (${d.error}); trying again in ${wait?.secs ?? 0} s`;
    }
    case "committing":
      return "committing";
    default:
      return event.type;
  }
}
